import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, PhoneOff, Video } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { CallService, type CallSignal } from "./callService";
import "./call.css";

type ActiveCall = {
  callId: string;
  remoteId: string;
  video: boolean;
  phase: "calling" | "incoming" | "connected";
  offer?: RTCSessionDescriptionInit;
};
export function CallHost({ userId }: { userId: string }) {
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [local, setLocal] = useState<MediaStream | null>(null);
  const [remote, setRemote] = useState<MediaStream | null>(null);
  const [muted, setMuted] = useState(false);
  const service = useRef<CallService | null>(null);
  const localVideo = useRef<HTMLVideoElement>(null);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (localVideo.current) localVideo.current.srcObject = local;
    if (remoteVideo.current) remoteVideo.current.srcObject = remote;
  }, [local, remote]);
  const close = async (status: "ended" | "declined" | "busy" = "ended") => {
    await service.current?.end(status);
    service.current = null;
    local?.getTracks().forEach((t) => t.stop());
    setLocal(null);
    setRemote(null);
    setCall(null);
  };
  useEffect(() => {
    const receive = async (signal: CallSignal) => {
      if (signal.signal_type === "invite") {
        setCall({
          callId: signal.call_id,
          remoteId: signal.sender_id,
          video: Boolean(signal.payload.video),
          phase: "incoming",
        });
        return;
      }
      if (signal.signal_type === "offer") {
        setCall((current) =>
          current?.callId === signal.call_id
            ? {
                ...current,
                offer: signal.payload as unknown as RTCSessionDescriptionInit,
              }
            : current,
        );
        return;
      }
      if (
        signal.signal_type === "end" ||
        signal.signal_type === "decline" ||
        signal.signal_type === "busy"
      ) {
        await close();
        return;
      }
      await service.current?.receive(signal);
    };
    const channel = supabase
      .channel(`call-signals:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "call_signals",
          filter: `recipient_id=eq.${userId}`,
        },
        (event) => void receive(event.new as CallSignal),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
      void close();
    };
  }, [userId]);
  useEffect(() => {
    const begin = async (event: Event) => {
      const detail = (
        event as CustomEvent<{ conversationId: string; video: boolean }>
      ).detail;
      if (!detail || call) return;
      try {
        const { data: members, error } = await supabase
          .from("conversation_members")
          .select("user_id")
          .eq("conversation_id", detail.conversationId)
          .neq("user_id", userId);
        if (error) throw error;
        const recipient = (members ?? [])[0]?.user_id as string | undefined;
        if (!recipient)
          throw new Error("Calls currently require a direct conversation.");
        const next = new CallService(userId, recipient, setRemote);
        service.current = next;
        const started = await next.start(detail.conversationId, detail.video);
        setLocal(started.stream);
        setCall({
          callId: started.callId,
          remoteId: recipient,
          video: detail.video,
          phase: "calling",
        });
      } catch (error) {
        window.dispatchEvent(
          new CustomEvent("ami:notice", {
            detail:
              error instanceof Error ? error.message : "Could not start call",
          }),
        );
      }
    };
    window.addEventListener("ami:start-call", begin);
    return () => window.removeEventListener("ami:start-call", begin);
  }, [call, userId]);
  const accept = async () => {
    if (!call?.offer) return;
    try {
      const next = new CallService(userId, call.remoteId, setRemote);
      service.current = next;
      const stream = await next.accept(call.callId, call.video, call.offer);
      setLocal(stream);
      setCall((c) => (c ? { ...c, phase: "connected" } : c));
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent("ami:notice", {
          detail:
            error instanceof Error ? error.message : "Unable to accept call",
        }),
      );
      await close("declined");
    }
  };
  const toggleMute = () => {
    local?.getAudioTracks().forEach((track) => (track.enabled = muted));
    setMuted((v) => !v);
  };
  if (!call) return null;
  return (
    <div className="callOverlay" role="dialog" aria-modal="true">
      <section className="callCard">
        <div className="avatar large">C</div>
        <h2>
          {call.phase === "incoming"
            ? "Incoming call"
            : call.phase === "calling"
              ? "Calling…"
              : "Connected"}
        </h2>
        <p>{call.video ? "Video call" : "Audio call"}</p>
        {call.video && (
          <div className="videos">
            <video ref={remoteVideo} autoPlay playsInline />
            <video ref={localVideo} autoPlay muted playsInline />
          </div>
        )}
        <div className="callControls">
          {call.phase === "incoming" ? (
            <>
              <button
                className="accept"
                disabled={!call.offer}
                onClick={() => void accept()}
              >
                <Video /> Accept
              </button>
              <button className="hangup" onClick={() => void close("declined")}>
                <PhoneOff /> Decline
              </button>
            </>
          ) : (
            <>
              <button onClick={toggleMute}>
                {muted ? <MicOff /> : <Mic />}
              </button>
              <button className="hangup" onClick={() => void close()}>
                <PhoneOff /> End
              </button>
              {call.video && (
                <button>
                  <Video />
                </button>
              )}
            </>
          )}
        </div>
        {call.phase === "incoming" && !call.offer && (
          <small>Connecting call signal…</small>
        )}
      </section>
    </div>
  );
}
